import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { EmptyState } from "@/components/DataTable";
import { PageHead } from "@/components/Filters";
import { api, errMsg, inr } from "@/lib/api";
import { useFilterOptions } from "./hooks";

const compareKey = (item) => `${item.college}|${item.branch || ""}`;

export default function Compare() {
  const opts = useFilterOptions();
  const [picks, setPicks] = useState([]);
  const [college, setCollege] = useState("");
  const [branch, setBranch] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get("/watchlist")
      .then(({ data }) => {
        const seen = new Set();
        const unique = [];
        for (const w of data) {
          const key = `${w.college}|${w.branch || ""}`;
          if (seen.has(key)) continue;
          seen.add(key);
          unique.push({ college: w.college, branch: w.branch || null });
          if (unique.length === 4) break;
        }
        setPicks(unique);
      })
      .catch(() => {});
  }, []);

  const add = () => {
    if (!college) return toast.error("Pick a college");
    if (picks.length >= 6) return toast.error("Compare up to 6 options");
    const next = { college, branch: branch || null };
    if (picks.some((p) => p.college === next.college && p.branch === next.branch))
      return toast.error("Already added");
    setPicks((p) => [...p, next]);
    setBranch("");
  };

  const run = async () => {
    if (picks.length < 2) return toast.error("Add at least two options to compare");
    setBusy(true);
    try {
      const { data } = await api.post("/compare", { items: picks });
      setResult(data.items);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const ROWS = [
    ["City", (i) => i.city || "—"],
    ["Type", (i) => i.college_type || "—"],
    ["Opening rank", (i) => (i.best_opening_rank ? Number(i.best_opening_rank).toLocaleString("en-IN") : "—")],
    ["Closing rank", (i) => (i.best_closing_rank ? Number(i.best_closing_rank).toLocaleString("en-IN") : "—")],
    ["Seats (all rounds)", (i) => i.seats || "—"],
    ["Quotas", (i) => (i.quotas?.length ? i.quotas.join(", ") : "—")],
    ["Rounds", (i) => (i.rounds?.length ? i.rounds.join(", ") : "—")],
    ["Lowest total fee", (i) => inr(i.total_fee)],
  ];

  return (
    <div className="space-y-4" data-testid="compare-page">
      <PageHead title="Compare" subtitle="Put up to six college-branch options side by side on ranks, seats and fees." />

      <div className="flex flex-wrap items-end gap-3 border border-slate-200 bg-white p-3">
        <label className="flex w-full flex-col gap-1 sm:w-auto">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">College</span>
          <select
            data-testid="compare-college-select"
            value={college}
            onChange={(e) => setCollege(e.target.value)}
            className="h-8 w-full border border-slate-300 bg-white px-2 text-sm sm:w-auto sm:min-w-[260px]"
          >
            <option value="">Select college</option>
            {opts.colleges.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label className="flex w-full flex-col gap-1 sm:w-auto">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Branch (optional)</span>
          <select
            data-testid="compare-branch-select"
            value={branch}
            onChange={(e) => setBranch(e.target.value)}
            className="h-8 w-full border border-slate-300 bg-white px-2 text-sm sm:w-auto sm:min-w-[180px]"
          >
            <option value="">Any branch</option>
            {opts.branches.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
        </label>
        <button
          data-testid="compare-add-btn"
          onClick={add}
          className="flex h-8 items-center gap-1.5 border border-slate-300 px-3 text-xs font-semibold text-slate-700 hover:border-[#1A365D] hover:text-[#1A365D]"
        >
          <Plus className="h-3.5 w-3.5" /> Add
        </button>
        <button
          data-testid="compare-run-btn"
          disabled={busy}
          onClick={run}
          className="ml-auto h-8 bg-[#1A365D] px-4 text-xs font-semibold uppercase tracking-widest text-white hover:bg-[#122845] disabled:opacity-40"
        >
          {busy ? "Comparing…" : "Compare"}
        </button>
      </div>

      {picks.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="compare-picks">
          {picks.map((p, i) => (
            <span key={`${p.college}|${p.branch || ""}`} className="flex items-center gap-2 border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700">
              {p.college}
              {p.branch ? ` · ${p.branch}` : ""}
              <button data-testid={`remove-pick-${i}`} onClick={() => setPicks((x) => x.filter((_, j) => j !== i))}>
                <X className="h-3 w-3 text-slate-400 hover:text-rose-600" />
              </button>
            </span>
          ))}
        </div>
      )}

      {!result ? (
        <EmptyState testId="compare-empty" title="Nothing compared yet" hint="Add two or more college-branch options above and hit Compare." />
      ) : (
        <div className="overflow-auto border border-slate-200 bg-white" data-testid="compare-table">
          <table className="data-table">
            <thead>
              <tr>
                <th>Metric</th>
                {result.map((item) => (
                  <th key={compareKey(item)} className="max-w-[220px] truncate">
                    {item.college}
                    {item.branch ? ` · ${item.branch}` : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map(([label, get]) => (
                <tr key={label}>
                  <td className="bg-slate-50 font-semibold text-slate-700">{label}</td>
                  {result.map((item) => (
                    <td key={`${label}|${compareKey(item)}`} className="font-mono-num">
                      {get(item)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
